'use client';

import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { useState } from 'react';

function pretty(d?: Date | null) {
  return d ? format(d, 'LLL dd, yyyy') : 'Select date';
}

export type SingleDatePickerProps = {
  value: Date | null;
  onChange: (date: Date | null) => void;
  id?: string;
  ariaLabel?: string;
  buttonClassName?: string;
};

export function SingleDatePicker({
  value,
  onChange,
  id,
  ariaLabel = 'Pick a date',
  buttonClassName = 'w-full justify-between font-normal',
}: SingleDatePickerProps) {
  const [open, setOpen] = useState(false);
  const current = value;

  const [date] = useState<Date | undefined>(value ?? new Date());

  return (
    <div className="flex flex-col gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={buttonClassName}
            aria-label={ariaLabel}
          >
            {pretty(value)}
            <CalendarIcon className="size-4" aria-hidden="true" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            defaultMonth={current ?? date}
            selected={current ?? undefined}
            onSelect={(d) => {
              onChange(d ?? null);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
